import React, { useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, Phone, MessageSquare, Globe, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HEROES } from "../data/heroes";
import HeroNarrateButton from "./HeroNarrateButton";

const HeroRecommendations = ({ analysis, showCrisisBanner = true, entryId = null }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  if (!analysis) return null;

  const ranking = analysis.hero_ranking || [];
  const crisis = analysis.crisis_detected && showCrisisBanner && !dismissed;
  const crisisRes = analysis.crisis_resources;

  const handleAck = async () => {
    setDismissed(true);
    if (entryId) {
      try {
        await axios.post("/journal/crisis/acknowledge", { entry_id: entryId }, { withCredentials: true });
      } catch (e) {}
    }
  };

  return (
    <div className="space-y-4" data-testid="hero-recommendations">
      {/* Crisis Resources Banner */}
      {crisis && crisisRes && (
        <Card
          className="p-5 border-2 border-red-500 bg-gradient-to-r from-red-50 to-rose-50 shadow-lg"
          data-testid="crisis-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-lg font-extrabold text-red-700">You are not alone.</h3>
                <button
                  onClick={handleAck}
                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100"
                  aria-label="Acknowledge"
                  data-testid="crisis-dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-red-800 mb-3">{crisisRes.headline}</p>
              <div className="grid sm:grid-cols-3 gap-2">
                <a
                  href={`tel:${crisisRes.us_988?.tel}`}
                  className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-red-700 transition-colors"
                  data-testid="crisis-call-988"
                >
                  <Phone className="w-4 h-4" /> Call/Text 988
                </a>
                <a
                  href={`sms:${crisisRes.crisis_text?.sms}`}
                  className="flex items-center gap-2 bg-red-700 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-red-800 transition-colors"
                  data-testid="crisis-text"
                >
                  <MessageSquare className="w-4 h-4" /> Text 741741
                </a>
                <a
                  href={crisisRes.international}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-slate-700 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
                  data-testid="crisis-international"
                >
                  <Globe className="w-4 h-4" /> International
                </a>
              </div>
              <p className="text-xs text-red-700 mt-3 italic">
                Victor the Voice says: Reach out. One call can change everything. You matter.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Hero Ranking */}
      {ranking.length > 0 && (
        <Card className="p-5" data-testid="hero-ranking-card">
          <h3 className="font-bold text-gray-900 mb-1">Your Brain Superheroes recommend</h3>
          <p className="text-xs text-gray-500 mb-4">
            Based on your entry, here's who can help most right now — top recommendation highlighted.
          </p>
          <div className="space-y-3">
            {ranking.map((r, idx) => {
              const hero = HEROES[r.hero];
              const tr = t(`heroes.${r.hero}`, { returnObjects: true });
              const top = idx === 0;
              return (
                <div
                  key={r.hero}
                  data-testid={`hero-rec-${r.hero}`}
                  className={`rounded-xl p-4 border ${
                    top
                      ? `bg-gradient-to-r ${hero.soft} border-2`
                      : "bg-gray-50 border-gray-200 opacity-90"
                  }`}
                  style={top ? { borderColor: hero.accent } : {}}
                >
                  <div className="flex items-start gap-3">
                    <div className={`${hero.imageBg || 'bg-slate-900'} rounded-lg p-1.5 flex items-end justify-center w-20 h-24 flex-shrink-0`}>
                      <img
                        src={hero.image}
                        alt={tr.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <h4 className="font-bold text-gray-900">{tr.name}</h4>
                          <p className="text-xs uppercase tracking-wider text-gray-500">{tr.pathTitle}</p>
                        </div>
                        {top && (
                          <Badge className={`${hero.chip} border-0`}>Top match</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1 italic">"{r.message}"</p>
                      <div className="flex flex-wrap gap-2 mt-3 items-center">
                        <HeroNarrateButton
                          hero={r.hero}
                          text={r.message}
                          label={`Hear ${tr.name.split(" ")[0]}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => navigate(`/activities?hero=${r.hero}`)}
                          className={`bg-gradient-to-r ${hero.gradient} text-white border-0`}
                          data-testid={`hero-rec-enter-${r.hero}`}
                        >
                          Enter {tr.pathTitle} →
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default HeroRecommendations;
